import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../../firebase';
import { onAuthStateChanged, updateProfile } from 'firebase/auth';
import { signInWithEmail, signUpWithEmail, signInWithGoogle } from '../../firebase/auth';
import { repositories } from '../../infrastructure/config/repositories';
import { User } from '../../domain/users/entities/User';
import { useCases } from '../di/useCases';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const createFirestoreUser = async (firebaseUser) => {
    try {
      const displayName = firebaseUser.displayName || 
                         (firebaseUser.email ? firebaseUser.email.split('@')[0] : 'Utilisateur');

      const newUser = new User({
        uid: firebaseUser.uid,
        displayName: displayName,
        email: firebaseUser.email || '',
        photoURL: firebaseUser.photoURL || null,
        joinDate: new Date().toISOString(),
        following: [],
        followers: [],
        participatingActivities: [],
        createdActivities: []
      });

      await repositories.users.createUser(newUser);
      return newUser;
    } catch (error) {
      console.error('Error creating Firestore user:', error);
      throw error;
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          let firestoreUser = null;
          try {
            firestoreUser = await repositories.users.getUser(firebaseUser.uid);
          } catch (error) {
            firestoreUser = await createFirestoreUser(firebaseUser);
          }
          
          firebaseUser.getIdToken().then(token => {
            localStorage.setItem('auth_token', token);
          });
          
          setUser(firestoreUser);
          
          if (window.location.pathname === '/') {
            navigate('/dashboard');
          }
        } catch (error) {
          console.error('Error in auth state change:', error);
          setUser(null);
        }
      } else {
        localStorage.removeItem('auth_token');
        setUser(null);
        if (window.location.pathname.startsWith('/dashboard')) {
          navigate('/');
        }
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [navigate]);

  const logout = async () => {
    try {
      await auth.signOut();
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  const refreshUser = useCallback(async () => {
    if (auth.currentUser) {
      try {
        const uid = auth.currentUser.uid;
        await new Promise(resolve => setTimeout(resolve, 300));
        
        try {
          const freshUserData = await useCases.getUserProfile.execute(uid);
        setUser(freshUserData);
          localStorage.setItem('last_user_refresh', Date.now().toString());
        return freshUserData;
        } catch (userError) {
          try {
            const directUserData = await repositories.users.getUser(uid);
            setUser(directUserData);
            return directUserData;
          } catch (directError) {
            console.error("Erreur lors de la récupération directe des données utilisateur:", directError);
            return null;
          }
        }
      } catch (error) {
        console.error("Erreur lors du rafraîchissement de l'utilisateur:", error);
        return null;
      }
    }
    return null;
  }, []);

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    setUser,
    signIn: async (email, password) => {
      try {
        await signInWithEmail(email, password);
      } catch (error) {
        throw error;
      }
    },
    signUp: async (email, password, name) => {
      try {
        const { user: firebaseUser } = await signUpWithEmail(email, password);

        // Set displayName on Firebase Auth profile (used by mobile + API)
        const resolvedName = name || firebaseUser.email.split('@')[0];
        await updateProfile(firebaseUser, { displayName: resolvedName });

        const newUser = new User({
          uid: firebaseUser.uid,
          displayName: resolvedName,
          email: firebaseUser.email,
          photoURL: firebaseUser.photoURL,
          joinDate: new Date().toISOString(),
          following: [],
          followers: [],
          participatingActivities: [],
          createdActivities: []
        });
        
        await repositories.users.createUser(newUser);
        return newUser;
      } catch (error) {
        throw error;
      }
    },
    signInWithGoogle: async () => {
      try {
        const { user: firebaseUser } = await signInWithGoogle();
        try {
          await repositories.users.getUser(firebaseUser.uid);
        } catch {
          await createFirestoreUser(firebaseUser);
        }
      } catch (error) {
        throw error;
      }
    },
    logout: logout,
    refreshUser: refreshUser
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 